import { Button } from 'components/Button';
import { Toggle } from 'components/Toggle';
import { generateId } from 'utils/id';
import { IncentiveItemEligibilityEditor } from './IncentiveItemEligibilityEditor';
import { emptyIncentiveItemSelectors } from './IncentiveSettings.itemEligibility';
import {
  ExchangeBonusRuleDraft,
  exchangeCreditCurrencies,
} from './ExchangeIncentiveSettings.policy';

export const newExchangeBonusRule = (
  currency: string,
  bonusPercentage = 0,
): ExchangeBonusRuleDraft => ({
  id: generateId(),
  name: 'Bonus rule',
  isEnabled: true,
  currency,
  minItemValue: 0,
  maxItemValue: null,
  requiresProtection: false,
  eligibility: {
    scope: 'ALL_ITEMS',
    include: emptyIncentiveItemSelectors(),
    exclude: emptyIncentiveItemSelectors(),
  },
  bonusPercentage,
});
export default function ExchangeBonusRuleEditor({
  rules,
  storeId,
  currency,
  disabled,
  onChange,
}: {
  rules: ExchangeBonusRuleDraft[];
  storeId: string;
  currency?: string;
  disabled: boolean;
  onChange: (rules: ExchangeBonusRuleDraft[]) => void;
}) {
  const update = (index: number, rule: ExchangeBonusRuleDraft) => {
    if (!disabled) onChange(rules.map((r, i) => (i === index ? rule : r)));
  };
  const move = (index: number, step: number) => {
    const to = index + step;
    if (disabled || to < 0 || to >= rules.length) return;
    const next = [...rules];
    [next[index], next[to]] = [next[to], next[index]];
    onChange(next);
  };
  const input =
    'mt-1 block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm disabled:bg-gray-50';
  return (
    <div className="space-y-4">
      <p className="text-xs leading-5 text-gray-500">
        Rules run from top to bottom. The first matching enabled rule applies;
        bonuses are not added together. Without a matching rule, eligible
        difference credit has no bonus.
      </p>
      {!rules.length && (
        <p className="rounded-lg bg-gray-50 p-3 text-sm text-gray-600">
          No bonus rules yet. Add a rule to configure a bonus.
        </p>
      )}
      {rules.map((rule, index) => {
        const prefix = `Bonus rule ${index + 1}`;
        return (
          <fieldset
            key={rule.id}
            disabled={disabled}
            className="space-y-4 rounded-lg border border-gray-200 p-4"
          >
            <legend className="px-1 text-sm font-semibold text-gray-900">
              {prefix}
            </legend>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <Toggle
                ariaLabel={`${prefix} enabled`}
                rightLabel={`${prefix} enabled`}
                isEnabled={rule.isEnabled}
                disabled={disabled}
                onChange={(isEnabled) => update(index, { ...rule, isEnabled })}
              />
              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  aria-label={`Move bonus rule ${index + 1} up`}
                  disabled={disabled || index === 0}
                  onClick={() => move(index, -1)}
                  className="text-xs font-medium text-gray-700 disabled:text-gray-400"
                >
                  Move up
                </button>
                <button
                  type="button"
                  aria-label={`Move bonus rule ${index + 1} down`}
                  disabled={disabled || index === rules.length - 1}
                  onClick={() => move(index, 1)}
                  className="text-xs font-medium text-gray-700 disabled:text-gray-400"
                >
                  Move down
                </button>
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => {
                    if (!disabled)
                      onChange(rules.filter((_, i) => i !== index));
                  }}
                  className="text-xs font-medium text-red-700"
                >
                  Remove bonus rule {index + 1}
                </button>
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_8rem]">
              <label className="text-sm font-medium">
                Rule name
                <input
                  className={input}
                  aria-label={`${prefix} name`}
                  maxLength={80}
                  value={rule.name}
                  onChange={(e) =>
                    update(index, { ...rule, name: e.target.value })
                  }
                />
              </label>
              <label className="text-sm font-medium">
                Currency
                <select
                  className={input}
                  aria-label={`${prefix} currency`}
                  value={rule.currency}
                  onChange={(e) =>
                    update(index, { ...rule, currency: e.target.value })
                  }
                >
                  {exchangeCreditCurrencies.map((code) => (
                    <option key={code}>{code}</option>
                  ))}
                </select>
              </label>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="text-sm font-medium">
                Minimum original item value
                <input
                  className={input}
                  inputMode="decimal"
                  aria-label={`${prefix} minimum item value`}
                  value={rule.minItemValue}
                  onChange={(e) =>
                    update(index, { ...rule, minItemValue: e.target.value })
                  }
                />
              </label>
              <label className="text-sm font-medium">
                Maximum original item value{' '}
                <span className="font-normal text-gray-500">(optional)</span>
                <input
                  className={input}
                  inputMode="decimal"
                  aria-label={`${prefix} maximum item value`}
                  placeholder="No maximum"
                  value={rule.maxItemValue ?? ''}
                  onChange={(e) =>
                    update(index, { ...rule, maxItemValue: e.target.value })
                  }
                />
              </label>
            </div>
            <p className="text-xs text-gray-500">
              Value limits use the original selected merchandise total in{' '}
              {rule.currency}. The bonus percentage applies to the eligible
              cheaper-exchange difference.
            </p>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                aria-label={`${prefix} requires SHIPAID Delivery Guarantee`}
                checked={rule.requiresProtection}
                onChange={(e) =>
                  update(index, {
                    ...rule,
                    requiresProtection: e.target.checked,
                  })
                }
              />
              Require SHIPAID Delivery Guarantee
            </label>
            <IncentiveItemEligibilityEditor
              value={rule.eligibility}
              storeId={storeId}
              ruleId={rule.id}
            ruleKind="bonus"
              onChange={(eligibility) =>
                update(index, { ...rule, eligibility })
              }
            />
            <label className="block max-w-sm text-sm font-medium">
              Bonus percentage
              <input
                className={input}
                inputMode="decimal"
                aria-label={`${prefix} percentage`}
                value={rule.bonusPercentage}
                onChange={(e) =>
                  update(index, { ...rule, bonusPercentage: e.target.value })
                }
              />
            </label>
            <p className="text-xs text-gray-500">
              Use 0% to 100%, with up to two decimal places. A matching 0% rule
              gives no bonus and stops evaluation.
            </p>
          </fieldset>
        );
      })}
      <Button
        variant="outlined"
        disabled={
          disabled ||
          rules.length >= 20 ||
          !currency ||
          !exchangeCreditCurrencies.includes(currency)
        }
        onClick={() => {
          if (
            !disabled &&
            rules.length < 20 &&
            currency &&
            exchangeCreditCurrencies.includes(currency)
          )
            onChange([...rules, newExchangeBonusRule(currency)]);
        }}
      >
        Add bonus rule
      </Button>
      {(!currency || !exchangeCreditCurrencies.includes(currency)) && (
        <p role="alert" className="text-xs text-amber-800">
          A supported store currency must be loaded before adding a rule.
        </p>
      )}
    </div>
  );
}
